import { useCallback, useMemo, useRef, useState } from 'react';
import type { Email, ThreadMessage } from '@/types/email';

type EmailLike = Email | ThreadMessage | null;

export interface RemoteImagesState {
  showRemoteImages: boolean;
  toggleRemoteImages: () => void;
  isBlockedSender: boolean;
}

export function useRemoteImages(email: EmailLike): RemoteImagesState {
  const emailId = email?.id ?? null;
  const isBlocked = email?.contactApprovalState === 'blocked';

  // Derived SYNCHRONOUSLY during render: the first committed frame must
  // already reflect the blocked-sender default. The old post-commit effect
  // left one frame where a blocked sender's srcdoc rendered with live remote
  // URLs — enough for tracking pixels to fire (D6).
  const defaultAllow = useMemo(() => {
    if (!emailId || typeof window === 'undefined') {
      return true;
    }
    const stored = getRemoteImagePreference(emailId);
    if (isBlocked) {
      return stored ?? false;
    }
    return stored ?? true;
  }, [emailId, isBlocked]);

  // Explicit user toggle for the CURRENT email only; falls back to the
  // derived default for any other message.
  const [override, setOverride] = useState<{ id: string; allow: boolean } | null>(null);
  const showRemoteImages = override && override.id === emailId ? override.allow : defaultAllow;

  const showRemoteImagesRef = useRef(showRemoteImages);
  showRemoteImagesRef.current = showRemoteImages;

  const toggleRemoteImages = useCallback(() => {
    if (!emailId) {
      return;
    }
    const next = !showRemoteImagesRef.current;
    setRemoteImagePreference(emailId, next);
    setOverride({ id: emailId, allow: next });
  }, [emailId]);

  return {
    showRemoteImages,
    toggleRemoteImages,
    isBlockedSender: isBlocked,
  };
}

const STORAGE_KEY = 'displayImages.preferences';
const STORAGE_VERSION = 1;
const MAX_REMOTE_IMAGE_PREFERENCES = 500;
const LEGACY_PREFIX = 'displayImages:';

interface RemoteImagePreference {
  allow: boolean;
  updatedAt: number;
}

interface RemoteImageStoragePayload {
  version: number;
  entries: Record<string, RemoteImagePreference>;
}

function parsePreferencePayload(raw: string | null): Record<string, RemoteImagePreference> {
  if (!raw) {
    return migrateLegacyPreferences();
  }
  try {
    const parsed = JSON.parse(raw) as RemoteImageStoragePayload;
    if (
      parsed &&
      typeof parsed === 'object' &&
      parsed.version === STORAGE_VERSION &&
      parsed.entries &&
      typeof parsed.entries === 'object'
    ) {
      return parsed.entries;
    }
  } catch (error) {
    console.warn('Failed to parse remote image preference payload', error);
  }
  return migrateLegacyPreferences();
}

function writePreferencePayload(entries: Record<string, RemoteImagePreference>) {
  const keys = Object.keys(entries);
  let entriesToPersist = entries;
  if (keys.length > MAX_REMOTE_IMAGE_PREFERENCES) {
    const sorted = keys.sort((a, b) => entries[b].updatedAt - entries[a].updatedAt);
    const pruned: Record<string, RemoteImagePreference> = {};
    sorted.slice(0, MAX_REMOTE_IMAGE_PREFERENCES).forEach((key) => {
      pruned[key] = entries[key];
    });
    entriesToPersist = pruned;
  }
  const payload: RemoteImageStoragePayload = {
    version: STORAGE_VERSION,
    entries: entriesToPersist,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn('Failed to persist remote image preferences', error);
  }
}

function migrateLegacyPreferences(): Record<string, RemoteImagePreference> {
  try {
    const entries: Record<string, RemoteImagePreference> = {};
    const legacyKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(LEGACY_PREFIX) && key !== STORAGE_KEY) {
        legacyKeys.push(key);
      }
    }
    if (!legacyKeys.length) {
      return entries;
    }
    const now = Date.now();
    legacyKeys.forEach((key) => {
      let value: string | null = null;
      try {
        value = localStorage.getItem(key);
      } catch (error) {
        console.warn('Failed to read legacy remote image preference', error);
      }
      if (value === null) {
        return;
      }
      const emailId = key.slice(LEGACY_PREFIX.length);
      entries[emailId] = {
        allow: value === 'true',
        updatedAt: now,
      };
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn('Failed to remove legacy remote image preference', error);
      }
    });
    writePreferencePayload(entries);
    return entries;
  } catch (error) {
    console.warn('Failed to migrate legacy remote image preferences', error);
    return {};
  }
}

function getRemoteImagePreference(emailId: string): boolean | null {
  if (typeof window === 'undefined') {
    return null;
  }
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to read remote image preferences', error);
    return null;
  }
  const payload = parsePreferencePayload(raw);
  const entry = payload[emailId];
  return typeof entry === 'object' ? entry.allow : null;
}

function setRemoteImagePreference(emailId: string, allow: boolean) {
  if (typeof window === 'undefined') {
    return;
  }
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to read remote image preferences before update', error);
  }
  const payload = parsePreferencePayload(raw);
  payload[emailId] = {
    allow,
    updatedAt: Date.now(),
  };
  writePreferencePayload(payload);
}
